import { Box, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import theme from '@/styles/theme';

import TabPanel from './TabPanel';
import { ITabBar } from './types';

/**
 * Dynamically usable Tab component: https://mui.com/material-ui/react-tabs/
 *
 * How to use:
 * Pass Array of Objects that have the following variables:
 * label: string -> Text displayed in label
 * input: any object -> is displayed when chosing the tab
 * index: number
 * id: string
 * -> index and id are used for differentiating if there are multiple tab components on one page
 */

interface Props {
  tabBarStyles?: React.CSSProperties;
  tapPanelStyles?: React.CSSProperties;
  content: ITabBar[];
  SideComponent?: React.ElementType;
  activeTabIndex?: number;
  sideComponentStyles?: React.CSSProperties;
  onTabChange?: (index: number) => void;
}

const TabBar = ({
  content,
  tabBarStyles = {},
  tapPanelStyles = {},
  SideComponent,
  activeTabIndex = 0,
  onTabChange,
  sideComponentStyles,
}: Props) => {
  const [value, setValue] = useState(activeTabIndex);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
      onTabChange?.(newValue);
    },
    [onTabChange]
  );

  useEffect(() => {
    setValue(activeTabIndex);
  }, [activeTabIndex]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          rowGap: '10px',
          ...tabBarStyles,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="tabs-container"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {content.map((t) =>
            t.to ? (
              <Tab
                sx={{
                  textTransform: 'none',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
                key={t.index}
                label={t.label}
                component={Link}
                to={t.to}
              />
            ) : (
              <Tab sx={{ textTransform: 'none' }} key={t.index} label={t.label} />
            )
          )}
        </Tabs>

        {SideComponent && (
          <Box
            sx={{
              minWidth: 'fit-content',
              zIndex: 1,
              ...sideComponentStyles,
            }}
          >
            <SideComponent />
          </Box>
        )}
      </Box>
      <>
        {content.map((t) => {
          return (
            t.index === value && (
              <TabPanel key={t.index} value={value} index={t.index} {...tapPanelStyles}>
                {t.input}
              </TabPanel>
            )
          );
        })}
      </>
    </Box>
  );
};
export default TabBar;
